import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

export interface Auth0PortalConfig {
	enabled: boolean;
	domain: string;
	clientId: string;
	audience: string;
}

const DISABLED_CONFIG: Auth0PortalConfig = {
	enabled: false,
	domain: '',
	clientId: '',
	audience: ''
};

@Injectable({ providedIn: 'root' })
export class PortalBootstrapService {
	// Auth0 settings are read per-portal from the Angular environment file (environment.auth0),
	// keyed by team code. They are public client identifiers, so they ship with the build.
	// loadForTeam() must be called with the active team before auth0Enabled is read (done by the
	// APP_INITIALIZER for deep-links and by TeamTemplateResolver for the start-at-root flow).
	public auth0Config: Auth0PortalConfig = DISABLED_CONFIG;

	public initialized = false;

	// Runs at APP_INITIALIZER — covers full-page loads where the team is already in the URL.
	public initialize(): Promise<void> {
		return this.loadForTeam(this.extractTeamCodeFromUrl());
	}

	// Applies the environment's Auth0 config for the given team code. Synchronous; returns a
	// resolved promise so the APP_INITIALIZER / resolver callers keep their existing contracts.
	public loadForTeam(teamCode: string | null): Promise<void> {
		this.auth0Config = this.resolveConfigForTeam(teamCode);
		this.initialized = true;
		return Promise.resolve();
	}

	public get auth0Enabled(): boolean {
		return this.auth0Config.enabled;
	}

	private resolveConfigForTeam(teamCode: string | null): Auth0PortalConfig {
		if (!teamCode) {
			return DISABLED_CONFIG;
		}

		const portals = (environment as any).auth0 || {};
		const env = portals[teamCode.toLowerCase()];
		if (!env) {
			// No entry for this portal: leave Auth0 disabled (legacy login).
			return DISABLED_CONFIG;
		}

		return {
			enabled: env.enabled === true || env.enabled === 'true',
			domain: env.domain || '',
			clientId: env.clientId || '',
			audience: env.audience || ''
		};
	}

	private extractTeamCodeFromUrl(): string | null {
		const path = window.location.pathname.replace(/^\//, '');
		if (!path) {
			return null;
		}
		const segments = path.split('/');
		return segments[0] || null;
	}
}

export function portalBootstrapInitializer(bootstrap: PortalBootstrapService): () => Promise<void> {
	return () => bootstrap.initialize();
}
